/**
 * Path / location (de)serialization for the react-router v3 shim.
 *
 * RR3 let callers express a destination either as a full path string
 * (`'/learn/foo?x=1#y'`) or as a location descriptor object
 * (`{pathname, query, hash, state}`). TanStack's `RouterHistory.push` takes a
 * single path string, so we normalize descriptors down to a string here, and
 * normalize TanStack's `HistoryLocation` back up to RR3's location shape.
 *
 * Query parsing uses `qs` (NOT TanStack's parser) so the `.query` object is
 * byte-for-byte what the app produced under RR3; the router is configured with
 * the same `qs` parse/stringify so internal state agrees (see main.js).
 */
import qs from 'qs';

import type {LocationDescriptor, LocationQuery, ShimLocation} from './types';

/**
 * Port of RR3's `formatPattern(pattern, params)`: substitutes `:name` tokens and
 * splats with `params`, dropping optional `(...)` groups whose params are absent.
 * Used to build URLs from a route pattern (e.g. ViewStudentSubmission).
 */
export function formatPattern(pattern: string, params: Record<string, unknown> = {}): string {
  let result = pattern.replace(/\(([^)]*)\)/g, (_match, inner: string) => {
    const tokens = inner.match(/:([a-zA-Z_$][\w$]*)/g) ?? [];
    const allPresent = tokens.every((token) => params[token.slice(1)] != null);
    return allPresent ? inner : '';
  });
  result = result.replace(/:([a-zA-Z_$][\w$]*)/g, (_match, name: string) => {
    const value = params[name];
    return value == null ? '' : String(value);
  });
  result = result.replace(/\*\*?/g, () => {
    const value = params.splat;
    return value == null ? '' : String(value);
  });
  return result;
}

/** Parse a raw search string (with or without leading `?`) into a qs object. */
export function parseQuery(search: string | undefined): LocationQuery {
  if (!search) {
    return {};
  }
  return qs.parse(search.replace(/^\?/, ''));
}

/** Stringify a qs object into a search string including the leading `?` (or ''). */
export function stringifyQuery(query: LocationQuery | undefined): string {
  if (!query) {
    return '';
  }
  const str = qs.stringify(query);
  return str ? `?${str}` : '';
}

function ensureLeading(char: string, value: string | undefined): string {
  if (!value) {
    return '';
  }
  return value.startsWith(char) ? value : `${char}${value}`;
}

/**
 * RR3 v3 had no relative-path routing: a string `to` (or descriptor `pathname`)
 * was always resolved as an absolute path from the root (e.g. `<Link
 * to="my-data/x/y">` → `/my-data/x/y`). TanStack's history, by contrast, stores a
 * pushed `'my-data/x/y'` verbatim as `location.pathname` (no leading slash) — the
 * browser URL bar still resolves to `/my-data/x/y`, but the router matches the
 * slash-less pathname and lands on the wrong route on SPA navigation (a reload,
 * which uses the real leading-slash URL, matches correctly). So restore RR3's
 * absolute semantics by prefixing `/` for ordinary paths, leaving external/scheme
 * URLs (`mailto:`, `http(s):`, protocol-relative `//`) and query-/hash-only
 * descriptors (which target the current path) untouched.
 */
function ensureAbsolute(path: string): string {
  if (path === '' || path.startsWith('/') || path.startsWith('?') || path.startsWith('#')) {
    return path;
  }
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(path)) {
    return path;
  }
  return `/${path}`;
}

/**
 * Collapse a `LocationDescriptor` (string or object) into a full path string
 * suitable for `RouterHistory.push`/`.replace`. A `query` object takes
 * precedence over a `search` string when both are present.
 */
export function serializePath(location: LocationDescriptor): string {
  if (typeof location === 'string') {
    return ensureAbsolute(location);
  }
  const pathname = location.pathname ?? '';
  const rawSearch =
    location.query !== undefined
      ? stringifyQuery(location.query)
      : ensureLeading('?', location.search);
  // A bare '?' (e.g. when all query params were removed) should serialize to no
  // search at all, so URLs don't get a dangling '?' (RR3 normalized this away).
  const search = rawSearch === '?' ? '' : rawSearch;
  const hash = ensureLeading('#', location.hash);
  return `${ensureAbsolute(pathname)}${search}${hash}`;
}

/** Extract the RR3 `state` bag from a location descriptor, if any. */
export function extractState(location: LocationDescriptor): ShimLocation['state'] {
  return typeof location === 'string' ? undefined : location.state;
}

/**
 * Split a serialized path string (`/path?search#hash`) into the
 * `{pathname, search, hash}` shape `toShimLocation` consumes — `search` keeps its
 * leading `?`, `hash` drops its leading `#` (toShimLocation re-adds it). Used to
 * build an optimistic location synchronously from a just-pushed path (see
 * history.ts), so `getCurrentLocation()` reflects a navigation immediately the way
 * RR3's history did.
 */
export function parseSerializedPath(path: string): {
  pathname: string;
  search: string;
  hash: string;
} {
  let rest = path;
  let hash = '';
  const hashIdx = rest.indexOf('#');
  if (hashIdx >= 0) {
    hash = rest.slice(hashIdx + 1);
    rest = rest.slice(0, hashIdx);
  }
  let search = '';
  const queryIdx = rest.indexOf('?');
  if (queryIdx >= 0) {
    search = rest.slice(queryIdx);
    rest = rest.slice(0, queryIdx);
  }
  return {pathname: rest, search, hash};
}

/**
 * Convert a TanStack `HistoryLocation` (`{pathname, search, hash, state, href}`,
 * where `search` is the raw string and `hash` excludes the leading `#`) into the
 * RR3 `location` shape that components read.
 */
export function toShimLocation(historyLocation: {
  pathname: string;
  search: string;
  hash: string;
  href?: string;
  state?: unknown;
}): ShimLocation {
  const search = historyLocation.search ?? '';
  const state = (historyLocation.state ?? null) as ShimLocation['state'];
  const key = state && typeof state === 'object' ? (state as {key?: string}).key : undefined;
  return {
    pathname: historyLocation.pathname,
    search,
    query: parseQuery(search),
    hash: ensureLeading('#', historyLocation.hash),
    state,
    key,
    href: historyLocation.href
  };
}
